"use client";

import React, { useState } from "react";
import { cn } from "@/lib/utils";
import type { BaseEntity, SimpleTableConfig, SimpleFieldConfig, FieldValue } from "../types";
import { renderCellValue } from "../utils";
import { getCategoryForField } from "../fieldRenderers";
import { useDetailActionContextOptional } from "@/contexts/DetailActionContext";

interface SimpleTableMobileDetailModalTabbedProps<T extends BaseEntity> {
  item: T;
  config: SimpleTableConfig<T>;
  onEdit?: (item: T) => void;
  onDelete?: (item: T) => void;
  onInlineEdit?: (item: T) => Promise<void>;
}

/**
 * Tabbed variant of mobile detail modal for SimpleTable
 * Organizes fields by category in tabs
 * Refactored to use existing utilities and helper functions
 */
export function SimpleTableMobileDetailModalTabbed<T extends { id: string }>({
  item,
  config,
  onEdit,
  onDelete,
  onInlineEdit,
}: SimpleTableMobileDetailModalTabbedProps<T>) {
  const [activeTab, setActiveTab] = useState<string | null>(null);

  // Get action context from provider (will be provided by UniversalDetailModalContainer)
  const actionContext = useDetailActionContextOptional<T>();

  // Group fields by category (exclude formOnly fields)
  const fieldsByCategory = React.useMemo(() => {
    return Object.entries(config.fields)
      .filter(([_, fieldConfig]) => !fieldConfig.formOnly)
      .reduce(
        (acc, [fieldId, fieldConfig]) => {
          const category = getCategoryForField(fieldId, fieldConfig);
          if (!acc[category]) {
            acc[category] = [];
          }
          acc[category].push([fieldId, fieldConfig]);
          return acc;
        },
        {} as Record<string, Array<[string, SimpleFieldConfig]>>
      );
  }, [config.fields]);

  const categories = Object.keys(fieldsByCategory);

  // Set first category as active on mount
  React.useEffect(() => {
    if (categories.length > 0 && !activeTab) {
      setActiveTab(categories[0]);
    }
  }, [categories, activeTab]);

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="flex space-x-1 bg-card-100 p-1 rounded-lg overflow-x-auto">
        {categories.map((category) => (
          <button type="button"
            key={category}
            onClick={() => setActiveTab(category)}
            className={cn(
              "px-3 py-2 text-sm font-medium rounded-md whitespace-nowrap transition-colors",
              activeTab === category
                ? "bg-card shadow-sm text-card-foreground"
                : "text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            )}
          >
            {category.charAt(0).toUpperCase() + category.slice(1)}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="space-y-4">
        {activeTab &&
          fieldsByCategory[activeTab]?.map(([fieldId, fieldConfig]) => {
            const value = item[fieldId as keyof T];

            return (
              <div key={fieldId} className="space-y-1">
                <label className="text-xs font-medium text-gray-600 dark:text-gray-400">{fieldConfig.label}</label>
                <div className="p-3 bg-card-50 rounded border text-sm min-h-[2.5rem]">
                  {value === null || value === undefined ? (
                    <span className="text-gray-400 italic">Not set</span>
                  ) : (
                    <div
                      className={cn(
                        "whitespace-pre-wrap break-words leading-relaxed word-break-break-word",
                        fieldConfig.type === "textarea" ||
                          fieldId.includes("description") ||
                          fieldId.includes("notes") ||
                          fieldId.includes("content")
                          ? "min-h-[3rem] py-1"
                          : ""
                      )}
                    >
                      {renderCellValue(value as FieldValue, fieldConfig, fieldId, false, {
                        entityRecord: item as Record<string, unknown>,
                        entityType: config.entity,
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
}
